import { HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ProgressLoaderAssurantService, ProgressLoaderService, RequestCriticalLevel, RequestCriticalLevelHelper } from 'digital-core';
import { ConfigService } from "./config.service";

// Will handle the loaders behaviours
@Injectable()
export class LoaderManagerService {
  private totalBlockingRequests = 0;
  private totalClassicRequests = 0;

  // Local configuration if exceptions needed
  //
  // You can filter the http requests that won't trigger any loader to show by providing an array of regex patterns or specific word contained ine the Url (and params):
  // like '\\d', '[a-zA-Z]' or 'resource', 'signingUrl', ...
  private _filteredUrlPatterns: string[] = [ ];
  // You can filter the http requests by providing an array of HTTP methods (case insensitive):
  // like 'gEt', 'POST', 'Put', 'options', ...
  private _filteredMethods: string[] = [];
  // You can also filter the http requests by providing an array of HTTP headers Name (doesn't work for a Header Value) (case insensitive):
  private _filteredHeaders: string[] = [];
  // if you just don't want to show any loader at all, probably for tests/debugging purpose
  private _forceByPass = false;

  constructor(
    public classicLoaderService: ProgressLoaderService, // classicLoader
    public blockingLoaderService: ProgressLoaderAssurantService,
    private configService: ConfigService
  ) {}

  handleEmptyRequestlevel(request: HttpRequest<any>): HttpRequest<any> {
    let dupReq = request;

    if (!this.hasRequestLevel(request)) {
      dupReq = request.clone({ headers: request.headers.set(RequestCriticalLevel.Default, "true") });
    }
    
    return dupReq;
  }

  private hasRequestLevel(request: HttpRequest<any>) {
    return RequestCriticalLevelHelper.keys().some(element => {
      return request.headers.has(element);
    });
  }

  // Wether the request will block the UI or not
  private shouldRequestBlockUI(request: HttpRequest<any>): boolean {
    return request.headers.has(RequestCriticalLevel.Critical);
  }

  //#region Managing START Behaviour

  // manage Loaders when request starts
  onRequestStart(request: HttpRequest<any>) {
    const shoulByPass = this.shouldBypass(request);

    if (!shoulByPass) {
      if (this.shouldRequestBlockUI(request)) {
        this.startBlockingLoader();
      } else {
        this.startClassicLoader();
      }
    }
  }

  // set no cache for avoid caching
  setNoCacheHeaders(request: HttpRequest<any>): HttpRequest<any> {
    let dupReq = request;

    dupReq = request.clone({ headers: request.headers.set('Cache-Control', 'no-cache')
    .set('Pragma', 'no-cache')
    .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
    .set('If-Modified-Since', '0') 
    .set('ClientId',this.configService.clientId)});
    
    return dupReq;
  }

  private startBlockingLoader() {
    this.totalBlockingRequests++;
    // force hide the classic one in case it's running.
    this.classicLoaderService.hide();
    this.blockingLoaderService.show();
  }

  private startClassicLoader() {
    this.totalClassicRequests++;
    // show this loader if only the blocking one is not running
    if (this.totalBlockingRequests === 0) { this.classicLoaderService.show(); }
  }

  //#endregion

  //#region Managing STOP Behaviour

  // manage Loaders when request stops
  onRequestStop(request: HttpRequest<any>) {
    if (this.shouldRequestBlockUI(request)) {
      this.stopBlockingLoader();
    } else {
      this.stopClassicLoader();
    }
  }

  private stopBlockingLoader() {
    // prevent blocking requests number < 0, should not be possible but let's add some firewall.
    if (this.totalBlockingRequests > 0) { this.totalBlockingRequests--; }
    // if there isn't blocking requests running, hide the blocking loader
    if (this.totalBlockingRequests === 0) {
      this.blockingLoaderService.hide();
      // if there is classic requests running, let's show the classic loader
      if (this.totalClassicRequests > 0) { this.classicLoaderService.show(); }
    }
  }

  private stopClassicLoader() {
    // prevent classic requests number < 0, should not be possible but let's add some firewall.
    if (this.totalClassicRequests > 0) {this.totalClassicRequests--; }
    // if there isn't classic requests running, hide the classic loader
    if (this.totalClassicRequests === 0) { this.classicLoaderService.hide(); }
  }

  //#endregion

  //#region ByPass

  private shouldBypassUrl(url: string): boolean {
    return this._filteredUrlPatterns.some(e => {
        let reg = new RegExp(e);
        return reg.test(url);
    });
  }

  private shouldBypassMethod(req: HttpRequest<any>): boolean {
    return this._filteredMethods.some(e => {
        return e.toUpperCase() === req.method.toUpperCase();
    });
  }

  private shouldBypassHeader(req: HttpRequest<any>): boolean {
    return this._filteredHeaders.some(e => {
        return req.headers.has(e);
    });
  }

  private shouldBypass(req: HttpRequest<any>): boolean {
    return this.shouldBypassUrl(req.urlWithParams)
        || this.shouldBypassMethod(req)
        || this.shouldBypassHeader(req)
        || this._forceByPass;
  }

  //#endregion
}
